import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { idbPersister, PERSONAL_QUERY_ROOTS, queryClient } from '@/lib/query-client';
import { refreshSession } from '@/lib/api';
import { changeLanguage } from '@/lib/i18n';
import { syncPushSubscription } from '@/lib/push';
import { useAuthStore } from '@/stores/auth';
import { usePreferences } from '@/stores/preferences';
import { toast, Toaster } from '@/components/ui';
import { router } from './router';

export function App() {
  const language = usePreferences((s) => s.language);

  useEffect(() => {
    void changeLanguage(language);
  }, [language]);

  useEffect(() => {
    // Restaure la session depuis le cookie de rafraîchissement (si l'utilisateur était connecté).
    const restore = useAuthStore.getState().user ? refreshSession().then((t) => (t ? syncPushSubscription() : undefined)) : Promise.resolve();
    void restore.finally(() => useAuthStore.getState().markRestored());
    // File d'envoi (Dexie) chargée après le premier rendu pour alléger le bundle initial.
    const idle = window.requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 1500));
    idle(() => {
      void import('@/lib/outbox').then((m) => m.startOutboxListeners((r) => toast.success('Envois effectués', `${r.sent} élément(s) en attente ont été envoyés.`)));
    });

    const onSwMessage = (e: MessageEvent<{ type?: string; url?: string }>) => {
      if (e.data?.type === 'NAVIGATE' && e.data.url) void router.navigate(new URL(e.data.url).pathname + new URL(e.data.url).search);
    };
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onSwMessage);
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: idbPersister,
        maxAge: 14 * 24 * 3_600_000,
        buster: __APP_VERSION__,
        dehydrateOptions: {
          shouldDehydrateQuery: (q) => q.state.status === 'success' && !PERSONAL_QUERY_ROOTS.includes(String(q.queryKey[0])),
        },
      }}
    >
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
      <Toaster />
    </PersistQueryClientProvider>
  );
}
